import { type HarmonyType } from "../../lib/colorEngine";

export interface ShuffleConstraints {
  harmonyMode: HarmonyType | undefined;
  hueMin: number | undefined;
  hueMax: number | undefined;
  lightnessMin: number | undefined;
  lightnessMax: number | undefined;
  saturationMin: number | undefined;
  saturationMax: number | undefined;
}

interface ShuffleOptionsPanelProps extends ShuffleConstraints {
  onHarmonyModeChange: (value: HarmonyType | undefined) => void;
  onHueMinChange: (value: number | undefined) => void;
  onHueMaxChange: (value: number | undefined) => void;
  onLightnessMinChange: (value: number | undefined) => void;
  onLightnessMaxChange: (value: number | undefined) => void;
  onSaturationMinChange: (value: number | undefined) => void;
  onSaturationMaxChange: (value: number | undefined) => void;
  onReset: () => void;
}

export function ShuffleOptionsPanel({
  harmonyMode,
  hueMin,
  hueMax,
  lightnessMin,
  lightnessMax,
  saturationMin,
  saturationMax,
  onHarmonyModeChange,
  onHueMinChange,
  onHueMaxChange,
  onLightnessMinChange,
  onLightnessMaxChange,
  onSaturationMinChange,
  onSaturationMaxChange,
  onReset,
}: ShuffleOptionsPanelProps) {
  return (
    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
      <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
        Shuffle Constraints
      </h4>
      <p className="text-xs text-gray-600 dark:text-gray-400 mb-4">
        Configure constraints to control how colors are shuffled. Locked colors
        (yellow border) will not be moved.
      </p>

      <div className="space-y-4">
        {/* Harmony Mode */}
        <div>
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
            Maintain Harmony
          </label>
          <select
            value={harmonyMode || ""}
            onChange={(e) =>
              onHarmonyModeChange(
                (e.target.value as HarmonyType) || undefined,
              )
            }
            className="pal-shuffle-field"
          >
            <option value="">None (Random shuffle)</option>
            <option value="analogous">Analogous</option>
            <option value="complementary">Complementary</option>
            <option value="triadic">Triadic</option>
            <option value="tetradic">Tetradic</option>
            <option value="splitComplementary">Split Complementary</option>
          </select>
        </div>

        {/* Hue Range */}
        <div>
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
            Hue Range (0-360°)
          </label>
          <div className="grid grid-cols-2 gap-2">
            <input
              type="number"
              min="0"
              max="360"
              placeholder="Min"
              value={hueMin ?? ""}
              onChange={(e) =>
                onHueMinChange(e.target.value ? Number(e.target.value) : undefined)
              }
              className="pal-shuffle-field"
            />
            <input
              type="number"
              min="0"
              max="360"
              placeholder="Max"
              value={hueMax ?? ""}
              onChange={(e) =>
                onHueMaxChange(e.target.value ? Number(e.target.value) : undefined)
              }
              className="pal-shuffle-field"
            />
          </div>
        </div>

        {/* Lightness Range */}
        <div>
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
            Lightness Range (0-100%)
          </label>
          <div className="grid grid-cols-2 gap-2">
            <input
              type="number"
              min="0"
              max="100"
              placeholder="Min"
              value={lightnessMin ?? ""}
              onChange={(e) =>
                onLightnessMinChange(
                  e.target.value ? Number(e.target.value) : undefined,
                )
              }
              className="pal-shuffle-field"
            />
            <input
              type="number"
              min="0"
              max="100"
              placeholder="Max"
              value={lightnessMax ?? ""}
              onChange={(e) =>
                onLightnessMaxChange(
                  e.target.value ? Number(e.target.value) : undefined,
                )
              }
              className="pal-shuffle-field"
            />
          </div>
        </div>

        {/* Saturation Range */}
        <div>
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
            Saturation Range (0-100%)
          </label>
          <div className="grid grid-cols-2 gap-2">
            <input
              type="number"
              min="0"
              max="100"
              placeholder="Min"
              value={saturationMin ?? ""}
              onChange={(e) =>
                onSaturationMinChange(
                  e.target.value ? Number(e.target.value) : undefined,
                )
              }
              className="pal-shuffle-field"
            />
            <input
              type="number"
              min="0"
              max="100"
              placeholder="Max"
              value={saturationMax ?? ""}
              onChange={(e) =>
                onSaturationMaxChange(
                  e.target.value ? Number(e.target.value) : undefined,
                )
              }
              className="pal-shuffle-field"
            />
          </div>
        </div>

        {/* Reset Button */}
        <button
          onClick={onReset}
          className="w-full px-3 py-2 bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 text-gray-900 dark:text-gray-100 rounded-lg text-sm transition-colors"
        >
          Reset All Constraints
        </button>
      </div>
    </div>
  );
}
